import React, { useState, useEffect, useRef } from "react";

import classNames from "classnames";
import { groupBy, isEmpty, sumBy } from "lodash-es";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom-v5-compat";

import { HelperText } from "../app/Atoms/Form/FormCopy";
import { TwoColumnPage, InfoPage } from "./Atoms";
import { TextField, Form, PriceField, Button } from "./Form";
import { useJourney } from "./sign_up_journeys";

const ReceiptPriceLine = ({ left, right }) => {
  const { t } = useTranslation("welcome");
  return (
    <div className="flex justify-between">
      <div>{left}</div>
      <div>{t("itemPreview.price", { price: right })}</div>
    </div>
  );
};

const ReportLinePreview = ({ posState }) => {
  const { t } = useTranslation("welcome");
  const report = groupBy(
    posState.history,
    ({ name, salesPrice }) => `${name}-${salesPrice}`,
  );
  return (
    <table className="w-full table-auto border-collapse rounded bg-white px-4 py-8 shadow">
      <thead>
        <tr className="border-b-2 border-neutral-300 px-4 text-xs">
          <th className="w-2/5 px-12 py-8 text-left">
            {t("itemPreview.reportPreview.itemName")}
          </th>
          <th className="px-12 py-8 text-right">
            {t("itemPreview.reportPreview.price")}
          </th>
          <th className="px-12 py-8 text-right">
            {t("itemPreview.reportPreview.count")}
          </th>
          <th className="px-12 py-8 text-right">
            {t("itemPreview.reportPreview.total")}
          </th>
        </tr>
      </thead>
      <tbody>
        {Object.values(report).map((orderedItems) => (
          <tr className="border-b border-neutral-200">
            <td className="flex-1 px-12 py-8 text-left">
              {orderedItems[0].name}
            </td>
            <td className="px-12 py-8 text-right">
              {orderedItems[0].salesPrice}
            </td>
            <td className="px-12 py-8 text-right">{orderedItems.length}</td>
            <td className="px-12 py-8 text-right">
              {sumBy(orderedItems, "salesPrice")}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
const Receipt = ({ receipt }) => {
  const { t } = useTranslation("welcome");
  const { journey } = useJourney();
  const totalPayment = receipt.reduce(
    (total, { salesPrice }) => total + salesPrice,
    0,
  );
  return (
    <div className="space-y-24 bg-white p-12">
      <div>
        <div>{journey.venueName}</div>
        <div>{t("itemPreview.receipt")}</div>
      </div>
      <div className="">
        {receipt.map((orderedItem) => (
          <ReceiptPriceLine
            left={orderedItem.name}
            right={orderedItem.salesPrice}
          />
        ))}
      </div>
      <div>
        <ReceiptPriceLine left={t("itemPreview.total")} right={totalPayment} />
        <ReceiptPriceLine
          left={t("itemPreview.cardPayment")}
          right={totalPayment}
        />
      </div>
    </div>
  );
};

const ReceiptPreview = ({ printContent, paperBottomRef }) => {
  const { t } = useTranslation("welcome");
  return (
    <div>
      <div
        className="relative mx-auto max-w-sm"
        style={{ paddingTop: "156px" }}
      >
        <div
          className="absolute inset-x-0 bottom-0 h-64"
          style={{ perspective: "400px" }}
        >
          <div
            className="absolute inset-x-0 bottom-0 h-[72px] w-full rounded-t bg-neutral-600"
            style={{
              transform: "translateZ(-6px) translateY(-69px) rotateX(10deg) ",
              transformOrigin: "bottom 50%",
            }}
          ></div>
          <div className="absolute inset-x-0 bottom-0 h-[72px] bg-neutral-800">
            <div className="flex h-full items-end justify-between px-24">
              <div className="items-start place-self-start pt-8 text-xs text-white">
                Your Printer
              </div>
              <div style={{ perspective: "400px" }}>
                <div
                  className="mb-4 flex items-end space-x-8 rounded-t px-12 pt-8"
                  style={{
                    transform: "rotateX(20deg) ",
                    transformOrigin: "bottom 50%",
                  }}
                >
                  <div className="h-16 w-4 animate-pulse rounded-t bg-green-400"></div>
                  <div className="h-16 w-4 rounded-t bg-neutral-400"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="relative bottom-[105px] h-192">
          <div
            ref={paperBottomRef}
            className="absolute inset-x-0 bottom-0 mx-auto max-h-[192px] w-4/5 overflow-auto border-b-2 border-neutral-800 text-center"
          >
            <div className="mx-auto flex h-[180px] items-center justify-center bg-white px-32 text-center text-xs text-neutral-500">
              {t("itemPreview.paperStart")}
            </div>{" "}
            <div className="mx-auto h-2 w-4 bg-white" />{" "}
            <div className="h-12 bg-white"></div>
            {printContent.map((receipt) => (
              <React.Fragment>
                <Receipt receipt={receipt} />
                <div>
                  <div className="mx-auto h-2 w-4 bg-white" />{" "}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MenuItem = ({ itemName, pingItem, ...props }) => {
  return (
    <div
      className={classNames(
        "align-center relative cursor-pointer items-center justify-center break-words rounded-b rounded-tr bg-white px-4 py-8 text-center shadow",
        {
          "animate-bounce delay-200": pingItem,
        },
      )}
      {...props}
    >
      {itemName}
    </div>
  );
};
const IpadPreview = ({
  pingItem,
  pingPay,
  posState: { item, orderedItems, onOrder, onPay, orderCount },
}) => {
  return (
    <div className="mx-auto flex aspect-ipad max-h-[384px] rounded-xl border-2 bg-white">
      <div className="w-2/3 space-y-8 rounded-l-lg bg-neutral-800 p-8">
        <div className="text-left text-xs text-white">Coffee</div>
        <div className="max-w-[124px]">
          {item.name && (
            <MenuItem
              pingItem={pingItem}
              onClick={() => onOrder(item)}
              itemName={item.name.substring(0, 20)}
            />
          )}
        </div>
      </div>
      <div className="flex min-h-0 w-1/3 flex-col">
        <div className="rounded-tr-lg bg-neutral-300 py-4 text-center text-xs">
          Order #{orderCount}
        </div>
        <div className="flex min-h-0 flex-1 flex-col justify-end">
          <div className="min-h-0 flex-1 overflow-scroll">
            <div className="">
              {orderedItems.map((item) => (
                <div className="border-b border-neutral-100 px-8 py-12 text-xs">
                  <ReceiptPriceLine
                    left={item.name.substring(0, 20)}
                    right={item.salesPrice}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="p-8">
            <button
              onClick={onPay}
              disabled={orderedItems.length == 0}
              className={classNames(
                "block w-full bg-orange-500 py-4 text-white disabled:cursor-not-allowed disabled:bg-orange-300",
                { "animate-bounce": pingPay },
              )}
            >
              Pay
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
const ItemPreview = ({ item, posState, paperBottomRef }) => {
  const { t } = useTranslation("welcome");
  return (
    <InfoPage title={<Trans t={t} i18nKey="itemPreview.title" />}>
      <ReceiptPreview
        printContent={posState.printContent}
        paperBottomRef={paperBottomRef}
      />
      <div className="space-y-8">
        <div>{t("itemPreview.reportingIsGreat")}</div>
        <ReportLinePreview posState={posState} />
      </div>
    </InfoPage>
  );
};
const ItemPreviewForm = ({ item, posState, onSubmit, formProps, addItem }) => {
  const { t } = useTranslation("welcome");

  const firstOrder = posState.orderCount == 1;
  const pingPay = firstOrder && posState.orderedItems.length > 0;
  const pingItem =
    !isEmpty(posState.item.name) &&
    posState.item.salesPrice &&
    firstOrder &&
    posState.orderedItems.length == 0;
  return (
    <Form onSubmit={onSubmit} className="space-y-48">
      <h1 className="text-center">{t("formContent.itemPreviewTitle")}</h1>
      <div className="flex w-full justify-around gap-48">
        <input
          type="hidden"
          value="pos-preview"
          {...formProps.register("currentPage")}
        />
        <TextField
          formProps={formProps}
          name="itemName"
          label={t("formContent.itemName")}
        />
        <PriceField
          formProps={formProps}
          name="itemSalesPrice"
          label={t("formContent.itemSalesPrice")}
        />
      </div>
      <div className="mx-auto w-full space-y-8">
        <IpadPreview
          pingItem={pingItem}
          pingPay={pingPay}
          posState={posState}
        />
        <div className="text-center">
          <HelperText>{t("itemPreview.makeAnOrder")}</HelperText>
        </div>
      </div>
      <div className="text-center">
        <Button primary>{t("itemPreview.nextButton")}</Button>
      </div>
    </Form>
  );
};
const PosBossPreview = ({ progress }) => {
  const { t } = useTranslation("welcome");
  const {
    journey,
    saveJourney,
    formProps: { handleSubmit, ...formProps },
  } = useJourney();

  const navigate = useNavigate();
  const onSubmit = async (data) => {
    await saveJourney(data);
    navigate("../going-live");
  };

  const paperBottomRef = useRef(null);
  const { itemSalesPrice, itemName, ...rest } = formProps.watch();
  const item = { name: itemName, salesPrice: itemSalesPrice || 0 };
  const [orderedItems, setOrderedItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [printContent, setPrintContent] = useState([]);
  const [orderCount, setOrderCount] = useState(1);
  const onOrder = (orderedItem) => {
    setOrderedItems([...orderedItems, orderedItem]);
  };

  useEffect(() => {
    paperBottomRef.current?.scrollTo({
      top: paperBottomRef.current?.scrollHeight + 400,
      behavior: "smooth",
    });
  }, [printContent]);

  const onPay = () => {
    setPrintContent([...printContent, orderedItems]);
    setHistory([...history, ...orderedItems]);
    setOrderedItems([]);
    setOrderCount(orderCount + 1);
  };

  const posState = {
    onPay,
    onOrder,
    history,
    orderedItems,
    printContent,
    orderCount,
    item,
  };

  return (
    <TwoColumnPage
      progress={progress}
      left={
        <ItemPreview
          item={item}
          posState={posState}
          paperBottomRef={paperBottomRef}
        />
      }
      right={
        <ItemPreviewForm
          item={item}
          posState={posState}
          onSubmit={handleSubmit(onSubmit)}
          formProps={formProps}
        />
      }
    />
  );
};

export default PosBossPreview;
